import RestClient from './RestClient';
import { Edition } from '../../models';
import * as _ from 'es-toolkit';
import { getApiErrorMessage } from './getApiErrorMessage';
import i18n from '../../locales/i18n';

class Editions extends RestClient {
    constructor() {
        super({
            model: Edition,
            entryPoint: 'edition',
            resource: 'edition',
            resources: 'editions',
            id_field: '_id',
            sortBy: 'start_date'
        });
    }

    /**
     * Kore exposes editions via No2 REST at GET /edition/list (see router alias `edition`).
     * Map 404 to an empty list for parity with the Engine when no editions exist.
     */
    list(params) {
        params = _.pickBy(params || {}, _.identity);

        let fieldList = params.fields;
        if (fieldList === undefined || fieldList === null) {
            fieldList = typeof this.model.getListingFields === 'function'
                ? this.model.getListingFields()
                : [];
        }
        if (!Array.isArray(fieldList))
            fieldList = [];

        const fields = fieldList
            .map((f) => String(f).trim())
            .filter((f) => f.length > 0 && /^[\w.]+$/.test(f))
            .join(',');

        const query = { ...params };
        if (fields)
            query.fields = fields;
        else
            delete query.fields;

        return this.GET(`/${this.entryPoint}${this.LIST_PATH}`, query)
            .then(async response => {
                if (response.status === 404)
                    return { editions: [] };

                const json = await response.json();

                if (!response.ok)
                    throw new Error(getApiErrorMessage(json));

                if (!json[this.resources])
                    return { editions: [] };

                json[this.resources] = json[this.resources].map(r => new this.model(r));

                if (this.sortBy)
                    json[this.resources] = _.sortBy(json[this.resources], this.sortBy).reverse();

                return json;
            });
    }

    getResourceCounts(editionId) {
        const id = String(editionId || '').trim();
        if (!id) {
            return Promise.reject(new Error('Invalid edition id'));
        }

        return this.GET(`/${this.entryPoint}/resource_counts/id/${encodeURIComponent(id)}`, {
            lang: i18n.language,
        })
            .then(async (response) => {
                const json = await response.json();
                if (!response.ok) {
                    throw new Error(getApiErrorMessage(json));
                }
                return json.resource_counts || { counts: {} };
            });
    }

    purgeResources(editionId) {
        const id = String(editionId || '').trim();
        if (!id) {
            return Promise.reject(new Error('Invalid edition id'));
        }

        return this.POST(`/${this.entryPoint}/purge_resources/id/${encodeURIComponent(id)}`, {
            lang: i18n.language,
        })
            .then(async (response) => {
                let json = {};
                try {
                    json = await response.json();
                } catch (e) {
                    if (!response.ok) {
                        throw new Error(getApiErrorMessage({}));
                    }
                    throw e;
                }
                if (!response.ok) {
                    throw new Error(getApiErrorMessage(json));
                }
                return json.purge || { deleted: {} };
            });
    }

    previewCopy({ sourceEdition, resources }) {
        const copyResources = Array.isArray(resources)
            ? resources.filter((key) => typeof key === 'string' && key.trim() !== '')
            : [];

        return this.POST(`/${this.entryPoint}/copy_preview`, {
            lang: i18n.language,
            copy_from_edition: sourceEdition,
            copy_resources: copyResources,
        })
            .then(async (response) => {
                let json = {};
                try {
                    json = await response.json();
                } catch (e) {
                    if (!response.ok) {
                        throw new Error(getApiErrorMessage({}));
                    }
                    throw e;
                }
                if (!response.ok) {
                    throw new Error(getApiErrorMessage(json));
                }
                return json.copy_preview || { counts: {} };
            });
    }

    create(payload, options = {}) {
        this.assertNotReadonlyEdition();

        payload = new this.model(payload);
        if ('prepareForUpdate' in payload)
            payload = payload.prepareForUpdate();

        const body = {
            lang: i18n.language,
            [this.resource]: payload
        };

        const copy = options?.copy || null;
        if (copy?.sourceEdition && Array.isArray(copy?.resources) && copy.resources.length > 0) {
            body.copy_from_edition = copy.sourceEdition;
            body.copy_resources = copy.resources;
            if (copy.adaptDates) {
                body.adapt_dates = true;
            }
        }

        return this.POST(`/${this.entryPoint}${this.CREATE_PATH}`, body)
            .then(response => response.json())
            .then(json => {
                if (json[this.resource]) {
                    json[this.resource] = new this.model(json[this.resource]);
                    return json;
                }
                throw new Error(getApiErrorMessage(json));
            });
    }
}

const EditionsApi = new Editions();

export default EditionsApi;
